import React, { useState, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { streamChatMessage, clearChatSession, stopChatSession, getChatHistory, getGoogleConnectionStatus } from '../services/api';
import FolderPicker from './FolderPicker';
import ShareButton from './ShareButton';
import MarkdownWithCharts from './MarkdownWithCharts';
import InteractiveChart from './InteractiveChart';
import { BrandAvatar, IconButton, LoadingDots } from './ui';
import { touchScrollColumn } from './ui/scrollStyles';
import { useViewport } from '../hooks/useViewport';
import GammaDeckCard, { GammaDeck, GammaProgress } from './GammaDeckCard';
import GammaBriefWizard, { GammaWizardData } from './GammaBriefWizard';
import PdfReportCard, { PdfReport } from './PdfReportCard';
import { ChatErrorBanner, isRateLimitError, rateLimitBanner, sendFailureBanner } from './chatSendError';

interface GeneratedImage {
  url: string;           // Firebase Storage public URL
  storagePath?: string;  // Path in Firebase Storage
  mimeType: string;
  prompt: string;
  aspectRatio: string;
  driveLink?: string;    // Optional Google Drive link if saved there
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  files?: Array<{
    name: string;
    content: string;
    mimeType: string;
    driveLink?: string;
  }>;
  folderPickerData?: {
    type: 'folder_picker_request';
    reason: string;
    searchContext?: string;
    folders: Array<any>;
    totalFolders: number;
  };
  charts?: Array<{
    type: 'chart_config';
    chartType: 'line' | 'bar' | 'pie' | 'donut' | 'area' | 'scatter' | 'radar' | 'bubble';
    config: any;
    title: string;
    width?: number;
    height?: number;
    horizontal?: boolean;
    stacked?: boolean;
  }>;
  generatedImages?: GeneratedImage[];
  gammaDecks?: GammaDeck[];
  pdfReports?: PdfReport[];
  gammaWizard?: {
    data: GammaWizardData;
    submitted?: boolean;
    submittedAnswers?: Record<string, string>;
  };
}

interface ChatWindowProps {
  selectedSessionId: string | null;
  userOrganization?: string; // 'culturegenesis' or 'partner'
  newChatTrigger?: number; // Fix: Trigger to force reset even when selectedSessionId is already null
  onSessionChange: (sessionId: string | null) => void;
  onShareSuccess?: (recipientName: string) => void;
  /** Scopes the agent to the Channel Intelligence "Ask" profile (curated tools + focused prompt). */
  agentProfile?: 'channel-intelligence';
  /** True when rendered mid-page (not filling the viewport) — disables viewport-edge compensation like safe-area padding. */
  embedded?: boolean;
}

// Error Boundary for message rendering
class MessageErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[MessageErrorBoundary] Caught error rendering message:', error);
    console.error('[MessageErrorBoundary] Error info:', errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '10px 14px',
          backgroundColor: 'var(--danger-soft)',
          border: '1px solid var(--cg-terra-200)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--danger)',
          fontSize: 13,
          fontWeight: 500
        }}>
          ⚠️ Failed to render this message. {this.state.error?.message || 'Unknown error'}
        </div>
      );
    }
    return this.props.children;
  }
}

const ChatWindow: React.FC<ChatWindowProps> = ({ selectedSessionId, userOrganization = 'culturegenesis', newChatTrigger, onSessionChange, onShareSuccess, agentProfile, embedded = false }) => {
  // Scoped Channel Intelligence "Ask" agent — drives a CI-specific welcome screen.
  const ciMode = agentProfile === 'channel-intelligence';
  const { isMobile } = useViewport();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [currentStreamContent, setCurrentStreamContent] = useState('');
  const [currentStreamFiles, setCurrentStreamFiles] = useState<Message['files']>([]);
  const [currentStreamCharts, setCurrentStreamCharts] = useState<Message['charts']>([]);
  const [currentStreamImages, setCurrentStreamImages] = useState<GeneratedImage[]>([]);
  const [currentStreamGammaDecks, setCurrentStreamGammaDecks] = useState<GammaDeck[]>([]);
  const [currentStreamPdfReports, setCurrentStreamPdfReports] = useState<PdfReport[]>([]);
  const [currentGammaProgress, setCurrentGammaProgress] = useState<GammaProgress | null>(null);
  const [currentGammaWizard, setCurrentGammaWizard] = useState<GammaWizardData | null>(null);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isSavingImageToDrive, setIsSavingImageToDrive] = useState(false);
  const [expandedImage, setExpandedImage] = useState<GeneratedImage | null>(null);
  const [currentFolderPicker, setCurrentFolderPicker] = useState<Message['folderPickerData'] | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [toolStatus, setToolStatus] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorAction, setErrorAction] = useState<string | null>(null);
  const [hasStartedChat, setHasStartedChat] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedAgent, setSelectedAgent] = useState<'claude' | 'openai'>('claude'); // Default to Claude
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileSizeWarning, setFileSizeWarning] = useState<{ type: 'warning' | 'info'; message: string } | null>(null);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [checkingGoogleConnection, setCheckingGoogleConnection] = useState(true);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const pinnedToBottomRef = useRef(true); // False while the user has scrolled up
  const scrollRafRef = useRef<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isStreamingRef = useRef(false); // Synchronous lock to prevent race conditions
  const stoppedByUserRef = useRef(false); // Track if user clicked stop button

  const scrollToBottom = () => {
    // Instant scroll only — a smooth animation fires its own scroll events,
    // which the pinned detector below would misread as the user scrolling
    // away, silently disabling auto-follow mid-stream. Scrolling the
    // container directly (not scrollIntoView) keeps ancestor scrollers and
    // in-flight iOS touch scrolling untouched. One pending frame at a time:
    // SSE chunks can commit faster than frames render.
    if (scrollRafRef.current !== null) cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      if (!pinnedToBottomRef.current) return; // User scrolled up since this was queued
      const el = messagesContainerRef.current;
      el?.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
    });
  };

  useEffect(() => {
    return () => {
      if (scrollRafRef.current !== null) cancelAnimationFrame(scrollRafRef.current);
    };
  }, []);

  const handleMessagesScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    pinnedToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  // File validation
  const validateFiles = (files: File[]): { valid: boolean; error?: string } => {
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25MB
    const ALLOWED_TYPES = [
      // Documents
      'text/plain',
      'application/pdf',
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      // Images (for Claude Vision analysis and Gemini reference)
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
    ];
    const ALLOWED_EXTENSIONS = ['txt', 'pdf', 'csv', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'webp'];

    // Check individual file sizes and types
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        return {
          valid: false,
          error: `File "${file.name}" exceeds 10MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB)`,
        };
      }

      const extension = file.name.toLowerCase().split('.').pop() || '';
      if (!ALLOWED_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(extension)) {
        return {
          valid: false,
          error: `File type not supported: "${file.name}". Allowed types: PDF, XLSX, CSV, TXT, JPG, PNG, GIF, WEBP`,
        };
      }
    }

    // Check total size
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > MAX_TOTAL_SIZE) {
      return {
        valid: false,
        error: `Total file size exceeds 25MB limit (${(totalSize / 1024 / 1024).toFixed(2)}MB)`,
      };
    }

    return { valid: true };
  };

  // Estimate if data files may be truncated (for warning display)
  const estimateFileTruncation = (files: File[]): { type: 'warning' | 'info'; message: string } | null => {
    const dataFiles = files.filter(f =>
      f.name.toLowerCase().endsWith('.csv') ||
      f.name.toLowerCase().endsWith('.xlsx') ||
      f.name.toLowerCase().endsWith('.xls')
    );

    if (dataFiles.length === 0) return null;

    const totalSize = dataFiles.reduce((sum, f) => sum + f.size, 0);

    // Warn if estimated content exceeds 400K chars (our new limit)
    if (totalSize > 400000) {
      return {
        type: 'warning',
        message: `Large file detected (~${(totalSize / 1024 / 1024).toFixed(1)}MB). Data may be partially truncated. Consider splitting into smaller files for complete processing.`
      };
    }

    // Info message for moderately large files (over 200KB)
    if (totalSize > 200000) {
      return {
        type: 'info',
        message: `Processing ${dataFiles.length} data file(s) (~${(totalSize / 1024).toFixed(0)}KB). This may take longer than usual.`
      };
    }

    return null;
  };

  // Handle file drop
  const onDrop = (acceptedFiles: File[]) => {
    setFileError(null);

    const newFiles = [...attachedFiles, ...acceptedFiles];
    const validation = validateFiles(newFiles);

    if (!validation.valid) {
      setFileError(validation.error!);
      return;
    }

    setAttachedFiles(newFiles);

    // Check for large file warning
    const warning = estimateFileTruncation(newFiles);
    setFileSizeWarning(warning);
  };

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true, // Only activate on drop, not click
    noKeyboard: true,
    accept: {
      // Documents
      'text/plain': ['.txt'],
      'application/pdf': ['.pdf'],
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      // Images (for Claude Vision analysis and Gemini reference)
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
    },
  });

  // Handle file input click
  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError(null);

    const files = Array.from(e.target.files || []);
    const newFiles = [...attachedFiles, ...files];
    const validation = validateFiles(newFiles);

    if (!validation.valid) {
      setFileError(validation.error!);
      return;
    }

    setAttachedFiles(newFiles);

    // Check for large file warning
    const warning = estimateFileTruncation(newFiles);
    setFileSizeWarning(warning);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove attached file
  const removeFile = (index: number) => {
    const newFiles = attachedFiles.filter((_, i) => i !== index);
    setAttachedFiles(newFiles);
    setFileError(null);

    // Recalculate warning for remaining files
    const warning = estimateFileTruncation(newFiles);
    setFileSizeWarning(warning);
  };

  // Auto-resize textarea
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 200); // Max 200px
      textarea.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputMessage]);

  useEffect(() => {
    // Follow new content only while the user is at the bottom — an
    // unconditional scroll cancels manual touch scrolling on iOS.
    if (pinnedToBottomRef.current) {
      scrollToBottom();
    }
  }, [messages, currentStreamContent]);

  // Check Google connection status
  useEffect(() => {
    const checkGoogleConnection = async () => {
      try {
        const status = await getGoogleConnectionStatus();
        console.log('[ChatWindow] Google connection status:', status);
        setGoogleConnected(status.connected);
      } catch (error) {
        console.error('[ChatWindow] Failed to check Google connection:', error);
        setGoogleConnected(false);
      } finally {
        setCheckingGoogleConnection(false);
      }
    };
    checkGoogleConnection();
  }, []);

  // Load chat history when a session is selected
  // Fix: Include newChatTrigger in dependency array to force reset even when selectedSessionId is already null
  useEffect(() => {
    // A session switch (or new chat) always lands at the newest message,
    // even if the user had scrolled up in the previous session.
    pinnedToBottomRef.current = true;
    const loadChatHistory = async () => {
      if (!selectedSessionId) {
        // New chat - clear everything
        console.log('[ChatWindow] Clearing messages for new chat (trigger:', newChatTrigger, ')');
        setMessages([]);
        setSessionId(null);
        setHasStartedChat(false);
        setAttachedFiles([]);
        setFileError(null);
        setErrorMessage(null);
        setErrorCode(null);
        setErrorAction(null);
        setCurrentStreamContent('');
        setCurrentStreamFiles([]);
        setCurrentStreamCharts([]);
        setCurrentStreamImages([]);
        setCurrentStreamGammaDecks([]);
        setCurrentStreamPdfReports([]);
        setCurrentGammaProgress(null);
        setCurrentGammaWizard(null);
        setCurrentFolderPicker(null);
        setToolStatus(null);
        onSessionChange(null);
        return;
      }

      // Load selected session's messages
      setIsLoadingHistory(true);
      try {
        const history = await getChatHistory(selectedSessionId);

        // Fix: Include all message fields (charts, folderPickerData) not just basic fields
        const formattedMessages: Message[] = history.messages.map((msg: any) => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp?._seconds ? new Date(msg.timestamp._seconds * 1000) : new Date(),
          files: msg.files,
          charts: msg.charts, // Fix: Include charts from history
          folderPickerData: msg.folderPickerData, // Fix: Include folderPickerData from history
          generatedImages: msg.generatedImages, // Fix: Include generated images from history
          pdfReports: msg.pdfReports // Include branded PDF report cards from history
        }));

        // FIX: Client-side deduplication - remove duplicate messages by ID or content
        // This is a safety net in case duplicates made it into Firestore
        const deduplicatedMessages = formattedMessages.filter((msg, index, arr) => {
          // First, check for duplicate IDs (keep first occurrence)
          const firstIndexWithSameId = arr.findIndex(m => m.id === msg.id);
          if (firstIndexWithSameId !== index) {
            console.warn('[ChatWindow] ⚠️ Duplicate message ID detected:', msg.id, '- removing duplicate');
            return false;
          }

          // Second, for assistant messages, check for duplicate content (same role + content)
          // This catches duplicates with different IDs
          if (msg.role === 'assistant' && msg.content) {
            const firstIndexWithSameContent = arr.findIndex(
              m => m.role === 'assistant' && m.content === msg.content
            );
            if (firstIndexWithSameContent !== index) {
              console.warn('[ChatWindow] ⚠️ Duplicate assistant message content detected - removing duplicate');
              console.warn('[ChatWindow] First occurrence ID:', arr[firstIndexWithSameContent].id);
              console.warn('[ChatWindow] Duplicate ID:', msg.id);
              return false;
            }
          }

          return true;
        });

        console.log('[ChatWindow] Loaded chat history:', history.messages.length, 'messages');
        if (deduplicatedMessages.length < formattedMessages.length) {
          console.warn('[ChatWindow] ⚠️ Removed', formattedMessages.length - deduplicatedMessages.length, 'duplicate message(s)');
        }
        console.log('[ChatWindow] After deduplication:', deduplicatedMessages.length, 'messages');

        setMessages(deduplicatedMessages);
        setSessionId(selectedSessionId);
        setHasStartedChat(formattedMessages.length > 0);
        onSessionChange(selectedSessionId);
      } catch (error) {
        console.error('Failed to load chat history:', error);
        alert('Failed to load chat history. Please try again.');
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadChatHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSessionId, newChatTrigger]); // Fix: Added newChatTrigger to dependencies

  const handleDownloadFile = (file: { name: string; content: string; mimeType: string }) => {
    const blob = new Blob([file.content], { type: file.mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Download generated image from Gemini (now uses URL instead of base64)
  const handleDownloadGeneratedImage = async (image: GeneratedImage) => {
    const extension = image.mimeType.split('/')[1] || 'png';
    const fileName = `culture-genesis-image-${Date.now()}.${extension}`;

    try {
      console.log('[ChatWindow] Downloading image from:', image.url);

      // Fetch the image from Firebase Storage URL
      // Firebase Storage public URLs should work with CORS
      const response = await fetch(image.url, { mode: 'cors' });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log('[ChatWindow] Image blob created:', blob.size, 'bytes');

      // Trigger download using blob URL
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileName;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();

      // Clean up after a short delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      }, 100);

      console.log('[ChatWindow] Download initiated for:', fileName);
    } catch (error) {
      console.error('[ChatWindow] Failed to download image:', error);

      // Fallback: open in new tab for manual save
      console.log('[ChatWindow] Trying fallback: opening image in new tab');
      window.open(image.url, '_blank');
      setErrorMessage('Download failed. Image opened in new tab - right-click to save.');
    }
  };

  // Save generated image to Google Drive
  // messageId is optional - if not provided, updates currentStreamImages; otherwise updates the message
  const handleSaveImageToDrive = async (image: GeneratedImage, imageIndex: number, messageId?: string) => {
    if (!sessionId) return;

    // Check if Google is connected
    if (!googleConnected) {
      setErrorMessage('Please connect Google Workspace to save images to Drive.');
      setErrorCode('GOOGLE_NOT_CONNECTED');
      return;
    }

    setIsSavingImageToDrive(true);
    try {
      // Fetch the image from Firebase Storage URL and convert to base64
      const response = await fetch(image.url);
      const blob = await response.blob();
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
          const base64 = dataUrl.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const base64 = await base64Promise;

      const { saveImageToDrive } = await import('../services/api');
      const result = await saveImageToDrive(sessionId, {
        base64: base64,
        mimeType: image.mimeType,
        prompt: image.prompt,
      });

      if (result.driveLink) {
        if (messageId) {
          // Update image in historical message
          setMessages(prev => prev.map(msg => {
            if (msg.id === messageId && msg.generatedImages) {
              const updatedImages = [...msg.generatedImages];
              if (updatedImages[imageIndex]) {
                updatedImages[imageIndex] = { ...updatedImages[imageIndex], driveLink: result.driveLink };
              }
              return { ...msg, generatedImages: updatedImages };
            }
            return msg;
          }));
        } else {
          // Update image in current streaming images
          setCurrentStreamImages(prev => {
            const updated = [...prev];
            if (updated[imageIndex]) {
              updated[imageIndex] = { ...updated[imageIndex], driveLink: result.driveLink };
            }
            return updated;
          });
        }
        // Open the Drive link in a new tab
        window.open(result.driveLink, '_blank');
      }
    } catch (error: any) {
      console.error('[ChatWindow] Failed to save image to Drive:', error);
      if (error.message?.includes('Google') || error.message?.includes('token')) {
        setErrorMessage('Google Workspace connection expired. Please reconnect.');
        setErrorCode('GOOGLE_TOKEN_EXPIRED');
      } else {
        setErrorMessage('Failed to save image to Google Drive. Please try again.');
      }
    } finally {
      setIsSavingImageToDrive(false);
    }
  };

  const handleClearChat = async () => {
    if (!sessionId) return;

    const confirmed = window.confirm('Clear all chat history and start a new conversation?');
    if (!confirmed) return;

    try {
      await clearChatSession(sessionId, selectedAgent);
      setMessages([]);
      setSessionId(null);
      setCurrentStreamContent('');
      setCurrentStreamFiles([]);
      setCurrentStreamCharts([]);
      setCurrentStreamImages([]);
      setIsImageLoading(false);
      setExpandedImage(null);
      setCurrentFolderPicker(null);
      setAttachedFiles([]);
      setFileError(null);
      setErrorMessage(null);
      setErrorCode(null);
      setErrorAction(null);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      console.log('Chat cleared successfully');
    } catch (error) {
      console.error('Failed to clear chat:', error);
    }
  };

  const handleStopMessage = async () => {
    if (!sessionId) return;

    // Set flag IMMEDIATELY before async call to prevent race condition
    // The 'complete' handler will check this flag
    stoppedByUserRef.current = true;
    console.log('[ChatWindow] ⏹️ Stopping session:', sessionId);

    try {
      await stopChatSession(sessionId, selectedAgent);
      console.log('[ChatWindow] ⏹️ Stop request sent successfully');
    } catch (error) {
      console.error('[ChatWindow] Failed to stop session:', error);
      // Even if API call fails, the 'complete' event from backend will handle cleanup
    }
    // Note: Don't add message here - let the 'complete' handler do it
    // to avoid duplicate messages from race conditions
  };

  /**
   * Unified SSE stream handlers. The three send paths (main send, Gamma-wizard
   * continuation, folder-selection continuation) previously carried divergent
   * copies of this event switch and silently dropped events in continuation
   * turns (folder_picker / image_generating / generated_image / warning).
   * One factory per request closes over the accumulators so EVERY path handles
   * EVERY event type identically.
   */
  const createStreamHandlers = (logPrefix: string) => {
    let accumulatedContent = '';
    const accumulatedFiles: NonNullable<Message['files']> = [];
    const accumulatedCharts: NonNullable<Message['charts']> = [];
    const accumulatedImages: GeneratedImage[] = [];
    const accumulatedGammaDecks: GammaDeck[] = [];
    const accumulatedPdfReports: PdfReport[] = [];
    let accumulatedGammaWizard: GammaWizardData | null = null;
    let accumulatedFolderPicker: Message['folderPickerData'] = undefined;
    let assistantMessageIdFromBackend: string | null = null;

    const clearStreamingState = () => {
      setCurrentStreamContent('');
      setCurrentStreamFiles([]);
      setCurrentStreamCharts([]);
      setCurrentStreamImages([]);
      setCurrentStreamGammaDecks([]);
      setCurrentStreamPdfReports([]);
      setCurrentGammaProgress(null);
      setCurrentGammaWizard(null);
      setIsImageLoading(false);
      setCurrentFolderPicker(null);
      setToolStatus(null);
      setIsStreaming(false);
    };

    const handleStreamEvent = (data: any) => {
      if (data.type === 'connected') {
        setSessionId(data.sessionId);
        onSessionChange(data.sessionId);
      } else if (data.type === 'tool_status') {
        setToolStatus(data.status);
        console.log(`${logPrefix} Tool status:`, data.status, data.tool ? `(${data.tool})` : '');
      } else if (data.type === 'folder_picker') {
        console.log(`${logPrefix} ✅ Received folder_picker event (${data.data?.totalFolders} folders)`);
        accumulatedFolderPicker = data.data;
        setCurrentFolderPicker(data.data);
        setToolStatus(null);
      } else if (data.type === 'assistant' && data.message?.content) {
        // Clear tool status when assistant starts responding
        setToolStatus(null);
        for (const block of data.message.content) {
          if (block.type === 'text') {
            accumulatedContent += block.text;
            setCurrentStreamContent(accumulatedContent);
          }
        }
      } else if (data.type === 'warning') {
        // Tool failures are non-terminal: preserve the live response while the
        // agent recovers. Other advisories (e.g. maxTurns) remain inline.
        console.warn(`${logPrefix} ⚠️ Stream warning:`, data.message);
        if (data.errorCode === 'GOOGLE_WORKSPACE_NOT_CONNECTED') {
          setErrorMessage(data.message || 'A tool failed while processing this request.');
          setErrorCode(data.errorCode);
          setErrorAction(data.suggestedAction || null);
        } else if (data.errorCode) {
          const advisory = `> ⚠️ ${data.message || 'A tool encountered a problem. The assistant is continuing.'}`;
          accumulatedContent += (accumulatedContent.length > 0 ? '\n\n' : '') + advisory;
          setCurrentStreamContent(accumulatedContent);
        } else if (data.message) {
          accumulatedContent += (accumulatedContent.length > 0 ? '\n\n' : '') + data.message;
          setCurrentStreamContent(accumulatedContent);
        }
      } else if (data.type === 'file') {
        accumulatedFiles.push({
          name: data.file.name,
          content: data.file.content,
          mimeType: data.file.mimeType,
          driveLink: data.file.driveLink
        });
        setCurrentStreamFiles([...accumulatedFiles]);
      } else if (data.type === 'chart') {
        console.log(`${logPrefix} ✅ Received chart:`, data.chart?.chartType, '-', data.chart?.title);
        accumulatedCharts.push(data.chart);
        setCurrentStreamCharts([...accumulatedCharts]);
      } else if (data.type === 'image_generating') {
        console.log(`${logPrefix} 🖼️ Image generation started`);
        setIsImageLoading(true);
        setToolStatus('Generating image with Gemini AI...');
      } else if (data.type === 'generated_image') {
        console.log(`${logPrefix} ✅ Received generated image:`, data.image?.url);
        setIsImageLoading(false);
        setToolStatus(null);
        accumulatedImages.push({
          url: data.image.url,
          storagePath: data.image.storagePath,
          mimeType: data.image.mimeType,
          prompt: data.image.prompt,
          aspectRatio: data.image.aspectRatio,
        });
        setCurrentStreamImages([...accumulatedImages]);
      } else if (data.type === 'gamma_progress') {
        setCurrentGammaProgress(data.progress);
        setToolStatus(`Generating Gamma deck (${data.progress?.elapsedSeconds || 0}s)...`);
      } else if (data.type === 'gamma_deck') {
        console.log(`${logPrefix} ✅ Gamma deck ready:`, data.deck?.gammaUrl);
        setCurrentGammaProgress(null);
        setToolStatus(null);
        accumulatedGammaDecks.push(data.deck);
        setCurrentStreamGammaDecks([...accumulatedGammaDecks]);
      } else if (data.type === 'gamma_wizard') {
        console.log(`${logPrefix} 🧙 Gamma wizard:`, data.data?.topic);
        accumulatedGammaWizard = data.data;
        setCurrentGammaWizard(data.data);
        setToolStatus(null);
      } else if (data.type === 'pdf_report') {
        console.log(`${logPrefix} ✅ PDF report ready:`, data.report?.title, `(v${data.report?.version})`);
        setToolStatus(null);
        accumulatedPdfReports.push(data.report);
        setCurrentStreamPdfReports([...accumulatedPdfReports]);
      } else if (data.type === 'assistant_message_id') {
        assistantMessageIdFromBackend = data.messageId;
      } else if (data.type === 'complete') {
        console.log(`${logPrefix} Stream complete:`, {
          contentLength: accumulatedContent.length,
          filesCount: accumulatedFiles.length,
          chartsCount: accumulatedCharts.length,
          imagesCount: accumulatedImages.length,
          gammaDecksCount: accumulatedGammaDecks.length,
          pdfReportsCount: accumulatedPdfReports.length,
          hasFolderPicker: !!accumulatedFolderPicker
        });

        const hasAnything =
          accumulatedContent.length > 0 ||
          accumulatedFiles.length > 0 ||
          accumulatedCharts.length > 0 ||
          accumulatedImages.length > 0 ||
          accumulatedGammaDecks.length > 0 ||
          accumulatedPdfReports.length > 0 ||
          !!accumulatedGammaWizard ||
          !!accumulatedFolderPicker;

        if (hasAnything) {
          // Use the backend's Firestore ID so share functionality works
          const messageId = assistantMessageIdFromBackend || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

          const wasStoppedByUser = stoppedByUserRef.current;
          if (wasStoppedByUser) {
            stoppedByUserRef.current = false;
            console.log(`${logPrefix} ⏹️ Adding stopped-by-user marker to message`);
          }

          const assistantMessage: Message = {
            id: messageId,
            role: 'assistant',
            content: wasStoppedByUser && accumulatedContent.length > 0
              ? accumulatedContent + '\n\n*[Response stopped by user]*'
              : accumulatedContent,
            timestamp: new Date(),
            files: accumulatedFiles.length > 0 ? accumulatedFiles : undefined,
            charts: accumulatedCharts.length > 0 ? accumulatedCharts : undefined,
            generatedImages: accumulatedImages.length > 0 ? accumulatedImages : undefined,
            gammaDecks: accumulatedGammaDecks.length > 0 ? accumulatedGammaDecks : undefined,
            pdfReports: accumulatedPdfReports.length > 0 ? accumulatedPdfReports : undefined,
            gammaWizard: accumulatedGammaWizard ? { data: accumulatedGammaWizard } : undefined,
            folderPickerData: accumulatedFolderPicker
          };

          // Add message to state, then clear streaming state AFTER React commits
          setMessages((prev) => {
            const newMessages = [...prev, assistantMessage];
            requestAnimationFrame(() => clearStreamingState());
            return newMessages;
          });
        } else {
          console.warn(`${logPrefix} Stream complete but no content accumulated`);
          clearStreamingState();
        }
      } else if (data.type === 'error') {
        console.error(`${logPrefix} Stream error:`, data.error);
        setErrorMessage(data.error || 'An error occurred while processing your request. Please try again.');
        setErrorCode(data.errorCode || null);
        setErrorAction(data.suggestedAction || null);
        clearStreamingState();
      }
    };

    const handleStreamError = (error: any) => {
      console.error(`${logPrefix} Chat error:`, error);
      setErrorMessage('Failed to send message. Please check your connection and try again.');
      clearStreamingState();
    };

    return { handleStreamEvent, handleStreamError };
  };

  const showErrorBanner = ({ message, code, action }: ChatErrorBanner) => {
    setErrorMessage(message);
    setErrorCode(code);
    setErrorAction(action);
  };

  const handleSendMessage = async () => {
    // Allow sending if there's text OR files (not both empty)
    const hasText = inputMessage.trim().length > 0;
    const hasFiles = attachedFiles.length > 0;

    // FIX: Use ref for synchronous check to prevent race conditions
    // Refs update immediately (synchronous), state updates are async
    if ((!hasText && !hasFiles) || isStreamingRef.current) return;

    // CRITICAL: Set ref IMMEDIATELY before any async operations
    // This prevents double-clicks/double-Enter from starting multiple requests
    isStreamingRef.current = true;
    stoppedByUserRef.current = false; // Reset stop flag for new message
    setIsStreaming(true); // Update UI state (async, for button disable)

    console.log('[ChatWindow] 🔒 Streaming lock acquired');

    try {
      // Mark chat as started on first message
      if (!hasStartedChat) {
        setHasStartedChat(true);
      }

      const userMessage: Message = {
        // FIXED: Use more unique ID to avoid collisions
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        role: 'user',
        content: inputMessage || `[${attachedFiles.length} file(s) attached]`,
        timestamp: new Date(),
      };

      pinnedToBottomRef.current = true; // A new question always re-pins to bottom
      setMessages((prev) => [...prev, userMessage]);
      const messageCopy = inputMessage || `[${attachedFiles.length} file(s) attached]`;
      const filesCopy = [...attachedFiles];

      setInputMessage('');
      setAttachedFiles([]);
      setFileError(null);
      setCurrentStreamContent('');
      setCurrentStreamFiles([]);
      setCurrentStreamCharts([]);
      setCurrentStreamImages([]);
      setIsImageLoading(false);
      setCurrentFolderPicker(null);
      setErrorMessage(null);
      setErrorCode(null);
      setErrorAction(null);
      setToolStatus(null);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      const { handleStreamEvent, handleStreamError } = createStreamHandlers('[ChatWindow]');

      await streamChatMessage(
        messageCopy,
        sessionId,
        handleStreamEvent,
        handleStreamError,
        selectedAgent,
        filesCopy.length > 0 ? filesCopy : undefined,
        userMessage.id,  // Pass frontend-generated ID so backend uses it for Firestore
        agentProfile
      );
    } catch (error) {
      console.error('Failed to send message:', error);
      showErrorBanner(sendFailureBanner(error));
      setCurrentStreamContent('');
      setCurrentStreamFiles([]);
      setCurrentStreamCharts([]);
      setCurrentStreamGammaDecks([]);
      setCurrentGammaProgress(null);
      setCurrentGammaWizard(null);
      setCurrentFolderPicker(null);
      setToolStatus(null);
    } finally {
      // CRITICAL: Always release lock in finally block
      // This ensures lock is released even if errors occur
      isStreamingRef.current = false;
      setIsStreaming(false);
      console.log('[ChatWindow] 🔓 Streaming lock released');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleGammaWizardSubmit = async (
    answers: Record<string, string>,
    summary: string,
    sourceMessageId?: string
  ) => {
    if (isStreamingRef.current) {
      console.log('[GammaWizard] Already streaming, ignoring wizard submit');
      return;
    }

    console.log('[GammaWizard] User submitted wizard answers:', answers);

    // Mark the originating wizard as submitted so it renders read-only.
    if (sourceMessageId) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === sourceMessageId && m.gammaWizard
            ? { ...m, gammaWizard: { ...m.gammaWizard, submitted: true, submittedAnswers: answers } }
            : m
        )
      );
    }
    setCurrentGammaWizard(null);

    // Lock + send the answers as a synthetic user message.
    isStreamingRef.current = true;
    stoppedByUserRef.current = false;
    setIsStreaming(true);

    try {
      const userMessage: Message = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        role: 'user',
        content: summary,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setCurrentStreamContent('');
      setErrorMessage(null);
      setErrorCode(null);
      setErrorAction(null);
      setToolStatus(null);

      const { handleStreamEvent, handleStreamError } = createStreamHandlers('[GammaWizard]');

      await streamChatMessage(
        summary,
        sessionId,
        handleStreamEvent,
        handleStreamError,
        selectedAgent,
        undefined,
        userMessage.id,
        agentProfile
      );
    } catch (err) {
      console.error('[GammaWizard] Failed to submit wizard:', err);
      if (isRateLimitError(err)) {
        showErrorBanner(rateLimitBanner(err));
      } else {
        setErrorMessage('Failed to submit wizard. Please try again.');
      }
    } finally {
      isStreamingRef.current = false;
      setIsStreaming(false);
    }
  };

  const handleGammaDeckArchive = async (gammaId: string) => {
    if (isStreamingRef.current) {
      console.log('[GammaArchive] Already streaming, ignoring archive request');
      return;
    }
    console.log('[GammaArchive] User requested archive of', gammaId);
    const summary = `Please archive the Gamma deck with id "${gammaId}".`;
    // Reuse the wizard submit path — same shape: synthetic user message →
    // Claude calls archive_gamma_deck.
    await handleGammaWizardSubmit({}, summary);
  };

  const handleGammaWizardSkip = (sourceMessageId?: string) => {
    if (sourceMessageId) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === sourceMessageId && m.gammaWizard
            ? { ...m, gammaWizard: { ...m.gammaWizard, submitted: true } }
            : m
        )
      );
    }
    setCurrentGammaWizard(null);
  };

  const handleFolderSelect = async (folderId: string, folderName: string) => {
    console.log('[FolderPicker] User selected folder:', { folderId, folderName });

    // FIX: Check ref to prevent duplicate requests
    if (isStreamingRef.current) {
      console.log('[FolderPicker] Already streaming, ignoring folder selection');
      return;
    }

    // Set lock immediately
    isStreamingRef.current = true;
    stoppedByUserRef.current = false; // Reset stop flag for new message
    setIsStreaming(true);

    console.log('[FolderPicker] 🔒 Streaming lock acquired');

    try {
      // Send folder selection as a new user message
      const selectionMessage = `I selected folder "${folderName}" (ID: ${folderId}). Please search in this folder.`;

      // Add it as a user message
      const userMessage: Message = {
        // FIXED: Use more unique ID to avoid collisions
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        role: 'user',
        content: selectionMessage,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setCurrentStreamContent('');
      setCurrentStreamFiles([]);
      setCurrentFolderPicker(null);
      setErrorMessage(null);
      setErrorCode(null);
      setErrorAction(null);
      setToolStatus(null);
      const { handleStreamEvent, handleStreamError } = createStreamHandlers('[FolderSelect]');

      await streamChatMessage(
        selectionMessage,
        sessionId,
        handleStreamEvent,
        handleStreamError,
        selectedAgent,
        undefined,  // no files for folder selection
        userMessage.id,  // Pass frontend-generated ID so backend uses it for Firestore
        agentProfile
      );
    } catch (error) {
      console.error('Failed to send folder selection:', error);

      if (isRateLimitError(error)) {
        showErrorBanner(rateLimitBanner(error));
      } else {
        // Provide a helpful error message
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        setErrorMessage('Failed to process folder selection. ' + errorMsg);
        setErrorAction('Please try selecting a different folder or refresh the page.');
      }
      setCurrentStreamContent('');
      setCurrentStreamFiles([]);
      setCurrentStreamCharts([]);
      setCurrentFolderPicker(null);
      setToolStatus(null);
    } finally {
      // CRITICAL: Always release lock in finally block
      isStreamingRef.current = false;
      setIsStreaming(false);
      console.log('[FolderPicker] 🔓 Streaming lock released');
    }
  };

  return (
    <div style={styles.container}>
      {/* Welcome Screen - shown before any messages */}
      {!hasStartedChat && !isLoadingHistory && (
        <div style={styles.welcomeContainer}>
          <div style={styles.welcomeContent}>
            <BrandAvatar size={isMobile ? 44 : 56} style={{ marginBottom: isMobile ? 12 : 18 }} />
            <div style={styles.welcomeEyebrow}>{ciMode ? 'Channel Intelligence' : 'Culture Genius'}</div>
            <h1 style={isMobile ? { ...styles.welcomeTitle, fontSize: 26 } : styles.welcomeTitle}>{ciMode ? 'Ask about any channel or the network' : 'How can I help today?'}</h1>
            <p style={styles.welcomeDescription}>
              {ciMode ? (
                <>
                  I answer questions about Culture Genesis channels using the Channel Intelligence data:
                  YouTube performance, Direct Sold advertisers &amp; campaigns, ad-format mix, booked revenue,
                  and the channel CRM (category/vertical, format, CMS). Ask about one channel or the whole network.
                </>
              ) : userOrganization === 'partner' ? (
                <>
                  Your AI assistant for Culture Genesis Partnerships. Access the Culture Genesis Creator database
                  and draft contextual outreach for prospective ad clients — all in one conversation.
                </>
              ) : (
                <>
                  Your home for Culture Genesis data — channel performance, earnings, advertisers, campaigns,
                  and creator insights — with access to your Gmail, Google Drive, Calendar, and the web.
                </>
              )}
            </p>
            {!ciMode && userOrganization !== 'partner' && (checkingGoogleConnection || !googleConnected) && (
              <div style={styles.googleReminder}>
                <span style={styles.googleReminderIcon}>💡</span>
                <span style={styles.googleReminderText}>
                  {checkingGoogleConnection ? (
                    'Checking Google Workspace connection…'
                  ) : (
                    <>Sign in with your <strong>@culturegenesis.com</strong> Google account to access Gmail and Drive</>
                  )}
                </span>
              </div>
            )}
            {!hasStartedChat && (
              <div style={isMobile ? { ...styles.suggestionGrid, marginTop: 18 } : styles.suggestionGrid}>
                {(ciMode
                  ? [
                      { icon: '📺', text: 'Tell me everything about Agent 00' },
                      { icon: '🏷️', text: 'Which advertisers ran across the network last month?' },
                      { icon: '🔥', text: 'Which channels have the most direct-sold demand?' },
                      { icon: '🎬', text: "What's our :15 vs :30 ad-format mix this month?" },
                      { icon: '🧩', text: 'Which channels are Black-Owned Gaming?' },
                      { icon: '🌐', text: 'Which campaigns ran across multiple CMSs?' },
                    ]
                  : userOrganization === 'partner'
                  ? [
                      { icon: '✉️', text: 'Draft a follow-up email for the Sephora partnerships team' },
                      { icon: '📊', text: 'Find creators that match a beauty + Gen-Z audience' },
                      { icon: '📈', text: 'Summarize last week\'s creator outreach' },
                      { icon: '🔍', text: 'Look up the latest contact for Pepsi Brand' },
                    ]
                  : [
                      { icon: '💰', text: 'How much did Dad Jokes earn last month?' },
                      { icon: '📈', text: 'Which channels grew the most last month?' },
                      { icon: '🏷️', text: 'Who were our top advertisers last month?' },
                      { icon: '🎯', text: 'What campaigns are delivering and how are they pacing?' },
                      { icon: '📺', text: 'Tell me everything about the iShowSpeed channel' },
                      { icon: '✉️', text: 'Summarize emails from General Mills this week' },
                    ]
                )
                  // Fewer prompts on phones — six stacked cards buried the
                  // composer and made the intro a long scroll.
                  .slice(0, isMobile ? 3 : 6)
                  .map((p, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setInputMessage(p.text)}
                    style={styles.suggestionItem}
                  >
                    <span style={styles.suggestionIcon}>{p.icon}</span>
                    <span style={styles.suggestionText}>{p.text}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Compact Header - shown after chat starts (Reset button only) */}
      {hasStartedChat && sessionId && messages.length > 0 && (
        <div style={styles.compactHeader}>
          <div style={styles.compactHeaderContent}>
            <button onClick={handleClearChat} style={styles.clearButton} aria-label="Reset conversation">
              <span style={styles.clearIcon}>↻</span>
              <span>Reset</span>
            </button>
          </div>
        </div>
      )}

      {isLoadingHistory ? (
        <div style={styles.loadingContainer}>
          <div style={styles.loadingSpinner}></div>
          <p style={styles.loadingText}>Loading chat history...</p>
        </div>
      ) : hasStartedChat ? (
        <div
          style={styles.messagesContainer}
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
        >
        {messages.map((msg, index) => (
          <div key={msg.id} style={styles.messageRow}>
            {msg.role === 'user' ? (
              <div style={styles.userMessageContainer}>
                <div style={styles.userMessage}>
                  {msg.content}
                </div>
              </div>
            ) : (
              <div style={styles.assistantMessageContainer}>
                <BrandAvatar size={32} style={{ marginTop: 4 }} />
                <div style={styles.assistantContent}>
                  <MessageErrorBoundary>
                    <MarkdownWithCharts
                      content={msg.content}
                      onChartExport={(chartTitle, base64Image) => {
                        console.log(`[ChatWindow] Chart exported: ${chartTitle}`);
                        // Future: Could automatically save to Drive or include in PDF
                      }}
                    />
                  </MessageErrorBoundary>
                  {msg.files && msg.files.length > 0 && (
                    <div style={styles.filesContainer}>
                      {msg.files.map((file, index) => (
                        <div key={index} style={styles.fileCard}>
                          <div style={styles.fileInfo}>
                            <span style={styles.fileName}>{file.name}</span>
                            {file.driveLink && (
                              <a
                                href={file.driveLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={styles.driveLink}
                              >
                                Open in Drive →
                              </a>
                            )}
                          </div>
                          <button
                            onClick={() => handleDownloadFile(file)}
                            style={styles.downloadButton}
                          >
                            Download
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {msg.charts && msg.charts.length > 0 && (
                    <div>
                      {msg.charts.map((chart, index) => {
                        console.log('[ChatWindow] 📊 Rendering chart:', chart.chartType, '-', chart.title);
                        return (
                          <MessageErrorBoundary key={index}>
                            <InteractiveChart
                              config={chart.config}
                              title={chart.title}
                              chartType={chart.chartType}
                              width={chart.width}
                              height={chart.height}
                              onExport={(base64Image) => {
                                console.log(`[ChatWindow] Chart exported: ${chart.title}`);
                              }}
                            />
                          </MessageErrorBoundary>
                        );
                      })}
                    </div>
                  )}
                  {msg.generatedImages && msg.generatedImages.length > 0 && (
                    <div style={styles.generatedImagesContainer}>
                      {msg.generatedImages.map((image, imgIndex) => (
                        <div key={imgIndex} style={styles.generatedImageCard}>
                          <img
                            src={image.url}
                            alt={image.prompt}
                            style={styles.generatedImage}
                            onClick={() => setExpandedImage(image)}
                          />
                          <div style={styles.imagePrompt}>{image.prompt}</div>
                          <div style={styles.imageActions}>
                            <button
                              onClick={() => handleDownloadGeneratedImage(image)}
                              style={styles.imageActionButton}
                            >
                              Download
                            </button>
                            <button
                              onClick={() => handleSaveImageToDrive(image, imgIndex, msg.id)}
                              style={styles.imageActionButton}
                              disabled={isSavingImageToDrive || !!image.driveLink}
                            >
                              {isSavingImageToDrive ? 'Saving...' : (image.driveLink ? 'Saved ✓' : 'Save to Drive')}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {msg.gammaDecks && msg.gammaDecks.length > 0 && (
                    <div>
                      {msg.gammaDecks.map((deck, deckIndex) => (
                        <GammaDeckCard
                          key={deck.generationId || deckIndex}
                          deck={deck}
                          onArchive={handleGammaDeckArchive}
                        />
                      ))}
                    </div>
                  )}
                  {msg.pdfReports && msg.pdfReports.length > 0 && (
                    <div>
                      {msg.pdfReports.map((report, reportIndex) => (
                        <PdfReportCard key={report.id || reportIndex} report={report} />
                      ))}
                    </div>
                  )}
                  {msg.gammaWizard && (
                    <GammaBriefWizard
                      data={msg.gammaWizard.data}
                      submitted={msg.gammaWizard.submitted}
                      submittedAnswers={msg.gammaWizard.submittedAnswers}
                      onSubmit={(answers, summary) => handleGammaWizardSubmit(answers, summary, msg.id)}
                      onSkip={() => handleGammaWizardSkip(msg.id)}
                    />
                  )}
                  {msg.folderPickerData && (() => {
                    console.log('[ChatWindow] 🎯 Rendering FolderPicker for completed message!');
                    console.log('[ChatWindow] Message ID:', msg.id);
                    console.log('[ChatWindow] Folder picker data:', msg.folderPickerData);
                    return (
                      <FolderPicker
                        reason={msg.folderPickerData.reason}
                        folders={msg.folderPickerData.folders}
                        searchContext={msg.folderPickerData.searchContext}
                        onSelectFolder={handleFolderSelect}
                      />
                    );
                  })()}

                  {/* Add Share Button if this completes a message pair */}
                  {index > 0 && messages[index - 1].role === 'user' && sessionId && (
                    <div style={styles.shareButtonContainer}>
                      <ShareButton
                        messageIds={[messages[index - 1].id, msg.id]}
                        sessionId={sessionId}
                        onShareSuccess={(recipientName) => {
                          if (onShareSuccess) {
                            onShareSuccess(recipientName);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        {toolStatus && (
          <div style={styles.messageRow}>
            <div style={styles.assistantMessageContainer}>
              <BrandAvatar size={32} thinking style={{ marginTop: 4 }} />
              <div style={styles.toolStatusContainer}>
                <div style={styles.toolStatusSpinner}></div>
                <div style={styles.toolStatusContent}>
                  <span style={styles.toolStatusText}>{toolStatus}</span>
                  <span style={styles.toolStatusSubtext}>This may take a moment for large datasets…</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStreamContent && (
          <div style={styles.messageRow}>
            <div style={styles.assistantMessageContainer}>
              <BrandAvatar size={32} thinking={isStreaming} style={{ marginTop: 4 }} />
              <div style={{...styles.assistantContent, ...styles.fadeIn}}>
                <MessageErrorBoundary>
                  <MarkdownWithCharts
                    content={currentStreamContent}
                    onChartExport={(chartTitle, base64Image) => {
                      console.log(`[ChatWindow] Chart exported during stream: ${chartTitle}`);
                    }}
                  />
                </MessageErrorBoundary>
                {isStreaming && (
                  <div style={styles.streamingIndicator}>
                    <span style={styles.streamingDot}></span>
                    <span style={styles.streamingDot}></span>
                    <span style={styles.streamingDot}></span>
                  </div>
                )}
                {currentStreamFiles && currentStreamFiles.length > 0 && (
                  <div style={styles.filesContainer}>
                    {currentStreamFiles.map((file, index) => (
                      <div key={index} style={styles.fileCard}>
                        <div style={styles.fileInfo}>
                          <span style={styles.fileName}>{file.name}</span>
                          {file.driveLink && (
                            <a
                              href={file.driveLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={styles.driveLink}
                            >
                              Open in Drive →
                            </a>
                          )}
                        </div>
                        <button
                          onClick={() => handleDownloadFile(file)}
                          style={styles.downloadButton}
                        >
                          Download
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {currentStreamCharts && currentStreamCharts.length > 0 && (
                  <div>
                    {currentStreamCharts.map((chart, index) => {
                      console.log('[ChatWindow] 📊 Rendering streaming chart:', chart.chartType, '-', chart.title);
                      return (
                        <MessageErrorBoundary key={index}>
                          <InteractiveChart
                            config={chart.config}
                            title={chart.title}
                            chartType={chart.chartType}
                            width={chart.width}
                            height={chart.height}
                            onExport={(base64Image) => {
                              console.log(`[ChatWindow] Chart exported during stream: ${chart.title}`);
                            }}
                          />
                        </MessageErrorBoundary>
                      );
                    })}
                  </div>
                )}
                {/* Image loading skeleton */}
                {isImageLoading && (
                  <div className="image-loading-skeleton">
                    <div className="shimmer-wrapper">
                      <div className="shimmer"></div>
                    </div>
                    <span>Generating image...</span>
                  </div>
                )}
                {/* Streaming generated images */}
                {currentStreamImages && currentStreamImages.length > 0 && (
                  <div style={styles.generatedImagesContainer}>
                    {currentStreamImages.map((image, imgIndex) => (
                      <div key={imgIndex} style={styles.generatedImageCard}>
                        <img
                          src={image.url}
                          alt={image.prompt}
                          style={styles.generatedImage}
                          onClick={() => setExpandedImage(image)}
                        />
                        <div style={styles.imagePrompt}>{image.prompt}</div>
                        <div style={styles.imageActions}>
                          <button
                            onClick={() => handleDownloadGeneratedImage(image)}
                            style={styles.imageActionButton}
                          >
                            Download
                          </button>
                          <button
                            onClick={() => handleSaveImageToDrive(image, imgIndex)}
                            style={styles.imageActionButton}
                            disabled={isSavingImageToDrive || !!image.driveLink}
                          >
                            {isSavingImageToDrive ? 'Saving...' : (image.driveLink ? 'Saved ✓' : 'Save to Drive')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {currentStreamGammaDecks && currentStreamGammaDecks.length > 0 && (
                  <div>
                    {currentStreamGammaDecks.map((deck, deckIndex) => (
                      <GammaDeckCard
                        key={deck.generationId || deckIndex}
                        deck={deck}
                        onArchive={handleGammaDeckArchive}
                      />
                    ))}
                  </div>
                )}
                {currentStreamPdfReports && currentStreamPdfReports.length > 0 && (
                  <div>
                    {currentStreamPdfReports.map((report, reportIndex) => (
                      <PdfReportCard key={report.id || reportIndex} report={report} />
                    ))}
                  </div>
                )}
                {currentGammaProgress && (
                  <GammaDeckCard progress={currentGammaProgress} />
                )}
                {currentGammaWizard && (
                  <GammaBriefWizard
                    data={currentGammaWizard}
                    onSubmit={(answers, summary) => handleGammaWizardSubmit(answers, summary)}
                    onSkip={() => handleGammaWizardSkip()}
                  />
                )}
                {currentFolderPicker && (() => {
                  console.log('[ChatWindow] 🎯 Rendering FolderPicker during stream!');
                  console.log('[ChatWindow] Folder picker data:', currentFolderPicker);
                  return (
                    <FolderPicker
                      reason={currentFolderPicker.reason}
                      folders={currentFolderPicker.folders}
                      searchContext={currentFolderPicker.searchContext}
                      onSelectFolder={handleFolderSelect}
                    />
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {isStreaming && !currentStreamContent && !toolStatus && (
          <div style={styles.messageRow}>
            <div style={styles.assistantMessageContainer}>
              <BrandAvatar size={32} thinking style={{ marginTop: 4 }} />
              <div style={styles.assistantBubbleEmpty}>
                <LoadingDots tone="accent" size={7} />
              </div>
            </div>
          </div>
        )}

        {errorMessage && (
          <div style={{
            ...styles.errorContainer,
            ...(errorCode === 'GOOGLE_WORKSPACE_NOT_CONNECTED' ? styles.errorContainerWarning : {})
          }}>
            <div style={styles.errorIcon}>
              {errorCode === 'GOOGLE_WORKSPACE_NOT_CONNECTED' ? '🔐' : '⚠️'}
            </div>
            <div style={styles.errorContent}>
              <div style={styles.errorHeader}>
                <div style={styles.errorTitle}>
                  {errorCode === 'GOOGLE_WORKSPACE_NOT_CONNECTED' ? 'Google Workspace Not Connected' :
                   errorCode === 'SERIALIZATION_ERROR' ? 'Response Too Large' :
                   errorCode === 'TOOL_EXECUTION_FAILED' ? 'Tool Execution Failed' :
                   errorCode === 'RATE_LIMITED' ? 'Too Many Requests' :
                   'Error'}
                </div>
                {errorCode && errorCode !== 'GOOGLE_WORKSPACE_NOT_CONNECTED' && (
                  <div style={styles.errorCode}>{errorCode}</div>
                )}
              </div>
              <div style={styles.errorText}>{errorMessage}</div>
              {errorAction && (
                <div style={styles.errorAction}>
                  <span style={styles.errorActionIcon}>💡</span>
                  <span style={styles.errorActionText}>{errorAction}</span>
                </div>
              )}
            </div>
            <button
              onClick={() => {
                setErrorMessage(null);
                setErrorCode(null);
                setErrorAction(null);
              }}
              style={styles.errorCloseButton}
              aria-label="Close error message"
            >
              ✕
            </button>
          </div>
        )}

        </div>
      ) : null}

      <div
        style={{
          ...styles.inputContainer,
          // Safe-area padding only when the composer actually sits at the
          // viewport bottom — embedded instances (Channel Intelligence Ask
          // tab) render mid-page inside a bordered box.
          ...(embedded ? { paddingBottom: 18 } : {}),
        }}
        {...getRootProps()}
      >
        <input {...getInputProps()} />

        {/* Drag overlay */}
        {isDragActive && (
          <div style={styles.dragOverlay}>
            <div style={styles.dragOverlayContent}>
              📎 Drop files here to attach
            </div>
          </div>
        )}

        {/* File error message */}
        {fileError && (
          <div style={styles.fileError}>
            ⚠️ {fileError}
            <button onClick={() => setFileError(null)} style={styles.fileErrorClose}>✕</button>
          </div>
        )}

        {/* File size warning */}
        {fileSizeWarning && (
          <div style={fileSizeWarning.type === 'warning' ? styles.fileSizeWarning : styles.fileSizeInfo}>
            {fileSizeWarning.type === 'warning' ? '⚠️' : 'ℹ️'} {fileSizeWarning.message}
          </div>
        )}

        {/* Attached files chips */}
        {attachedFiles.length > 0 && (
          <div style={styles.fileChipsContainer}>
            {attachedFiles.map((file, index) => {
              const isImage = file.type.startsWith('image/');
              return (
                <div key={index} style={styles.fileChip}>
                  {isImage ? (
                    // Show image thumbnail for images
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      style={{
                        width: '32px',
                        height: '32px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        marginRight: '6px',
                      }}
                      onLoad={(e) => {
                        // Revoke object URL after image loads to free memory
                        URL.revokeObjectURL((e.target as HTMLImageElement).src);
                      }}
                    />
                  ) : (
                    <span style={styles.fileChipIcon}>
                      {file.type.includes('pdf') ? '📄' :
                       file.type.includes('csv') ? '📊' :
                       file.type.includes('spreadsheet') || file.type.includes('excel') ? '📊' :
                       '📝'}
                    </span>
                  )}
                  <span style={styles.fileChipName}>{file.name}</span>
                  <span style={styles.fileChipSize}>({(file.size / 1024).toFixed(1)}KB)</span>
                  <button
                    onClick={() => removeFile(index)}
                    style={styles.fileChipRemove}
                    title="Remove file"
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div style={styles.inputWrapper}>
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".txt,.pdf,.csv,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />

          <div style={styles.composerCard}>
            <IconButton
              label="Attach files (PDF, CSV, XLSX, TXT)"
              onClick={handleAttachClick}
              disabled={isStreaming}
              variant="ghost"
              size="md"
              style={{ alignSelf: 'flex-end' }}
            >
              📎
            </IconButton>

            <textarea
              ref={textareaRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask Culture Genius anything…"
              style={styles.input}
              disabled={isStreaming}
              rows={1}
            />

            {isStreaming && sessionId ? (
              <button
                onClick={handleStopMessage}
                style={styles.stopButton}
                title="Stop generating"
                aria-label="Stop generating"
              >
                ■
              </button>
            ) : (
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() && attachedFiles.length === 0}
                style={{
                  ...styles.sendButton,
                  ...(!inputMessage.trim() && attachedFiles.length === 0 ? styles.sendButtonDisabled : {}),
                }}
                aria-label="Send"
              >
                ↑
              </button>
            )}
          </div>
          <div style={styles.disclaimer}>
            Culture Genius can make mistakes. Verify important info.
          </div>
        </div>
      </div>

      {/* Expanded Image Modal */}
      {expandedImage && (
        <div
          className="image-modal-overlay"
          onClick={() => setExpandedImage(null)}
        >
          <button
            className="image-modal-close"
            onClick={(e) => {
              e.stopPropagation();
              setExpandedImage(null);
            }}
          >
            ✕
          </button>
          <img
            src={expandedImage.url}
            alt={expandedImage.prompt}
            className="image-modal-content"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: 'transparent',
    position: 'relative',
  },
  // ===== Welcome screen =====
  welcomeContainer: {
    // Scrolls when the intro + prompt cards are taller than the viewport
    // (phones), so the composer below stays anchored and tappable. Centering
    // comes from welcomeContent's `margin: auto` rather than align/justify:
    // those make overflow unreachable in the block direction.
    ...touchScrollColumn,
    display: 'flex',
    padding: '24px 0',
    animation: 'fadeIn 480ms var(--ease-standard)',
  },
  welcomeContent: {
    textAlign: 'center',
    maxWidth: 720,
    padding: '0 24px',
    margin: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    animation: 'fadeInUp 600ms var(--ease-standard) 80ms backwards',
  },
  welcomeEyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--accent)',
    marginBottom: 6,
  },
  welcomeTitle: {
    fontSize: 36,
    fontWeight: 700,
    color: 'var(--fg-1)',
    margin: 0,
    letterSpacing: '-0.022em',
    fontFamily: 'var(--font-display)',
    lineHeight: 1.1,
  },
  welcomeDescription: {
    fontSize: 15,
    lineHeight: 1.6,
    color: 'var(--fg-3)',
    margin: '8px 0 0',
    fontWeight: 400,
    maxWidth: 520,
  },
  googleReminder: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 20,
    padding: '10px 14px',
    background: 'var(--info-soft)',
    border: '1px solid var(--cg-teal-200)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--info)',
  },
  googleReminderIcon: { fontSize: 14, flexShrink: 0 },
  googleReminderText: {
    fontSize: 13,
    lineHeight: 1.5,
    color: 'var(--cg-teal-600)',
    fontWeight: 500,
  },
  suggestionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 10,
    width: '100%',
    marginTop: 28,
  },
  suggestionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: 'rgba(255,255,255,0.7)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    padding: '12px 14px',
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    transition: 'all 200ms var(--ease-standard)',
    textAlign: 'left',
  },
  suggestionIcon: { fontSize: 18, flexShrink: 0, lineHeight: 1 },
  suggestionText: { fontSize: 13, color: 'var(--fg-2)', textAlign: 'left' as const },

  // ===== Compact header =====
  compactHeader: {
    padding: '12px 24px',
    borderBottom: '1px solid var(--border-subtle)',
    backgroundColor: 'transparent',
    animation: 'fadeInUp 400ms var(--ease-standard)',
    flexShrink: 0,
  },
  compactHeaderContent: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    maxWidth: 900,
    margin: '0 auto',
  },
  clearButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    background: 'transparent',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--fg-2)',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    transition: 'all 220ms var(--ease-standard)',
  },
  clearIcon: { fontSize: 15, lineHeight: 1 },

  // ===== Messages =====
  messagesContainer: {
    ...touchScrollColumn,
    padding: '24px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },
  messageRow: {
    maxWidth: 900,
    width: '100%',
    margin: '0 auto',
    animation: 'fadeInUp 360ms var(--ease-standard)',
  },
  userMessageContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  userMessage: {
    maxWidth: '78%',
    padding: '12px 18px',
    background: 'var(--accent)',
    color: '#fff',
    borderRadius: 'var(--radius-xl)',
    borderBottomRightRadius: 'var(--radius-sm)',
    fontSize: 14.5,
    lineHeight: 1.55,
    fontWeight: 500,
    boxShadow: 'var(--shadow-sm)',
    whiteSpace: 'pre-wrap',
  },
  assistantMessageContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: 12,
    alignItems: 'flex-start',
    maxWidth: '92%',
  },
  assistantContent: {
    flex: 1,
    minWidth: 0,
    fontSize: 14.5,
    lineHeight: 1.65,
    color: 'var(--fg-1)',
    fontWeight: 400,
    background: 'var(--bg-surface-1)',
    border: '1px solid var(--border-1)',
    padding: '14px 18px',
    borderRadius: 'var(--radius-xl)',
    borderTopLeftRadius: 'var(--radius-sm)',
    boxShadow: 'var(--shadow-xs)',
  },
  assistantBubbleEmpty: {
    flex: 1,
    minWidth: 0,
    background: 'var(--bg-surface-1)',
    border: '1px solid var(--border-1)',
    padding: '14px 18px',
    borderRadius: 'var(--radius-xl)',
    borderTopLeftRadius: 'var(--radius-sm)',
    boxShadow: 'var(--shadow-xs)',
    display: 'flex',
    alignItems: 'center',
  },
  shareButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 10,
    paddingTop: 8,
    borderTop: '1px solid var(--border-subtle)',
    opacity: 0.7,
    transition: 'opacity 200ms',
  },
  fadeIn: { animation: 'fadeIn 320ms var(--ease-standard)' },
  streamingIndicator: {
    display: 'inline-flex',
    gap: 4,
    marginLeft: 6,
    alignItems: 'center',
    verticalAlign: 'middle',
  },
  streamingDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'var(--accent)',
    animation: 'cgPulse 1.2s ease-in-out infinite',
    display: 'inline-block',
  },

  // ===== File attachments inside messages =====
  filesContainer: {
    marginTop: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  fileCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    background: 'var(--accent-soft)',
    border: '1px solid transparent',
    borderRadius: 'var(--radius-md)',
    gap: 12,
    transition: 'all 220ms var(--ease-standard)',
  },
  fileInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
    minWidth: 0,
  },
  fileName: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--accent-soft-fg)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  driveLink: {
    fontSize: 12,
    color: 'var(--accent)',
    textDecoration: 'none',
    fontWeight: 500,
    transition: 'color 200ms',
  },
  downloadButton: {
    padding: '6px 12px',
    background: '#fff',
    color: 'var(--fg-1)',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    transition: 'all 220ms var(--ease-standard)',
    whiteSpace: 'nowrap',
    fontFamily: 'var(--font-sans)',
  },

  // ===== Composer =====
  inputContainer: {
    padding: '8px 16px 18px',
    paddingBottom: 'calc(18px + env(safe-area-inset-bottom))',
    background: 'rgba(255,255,255,0.7)',
    backdropFilter: 'var(--glass-panel)',
    WebkitBackdropFilter: 'var(--glass-panel)',
    borderTop: '1px solid var(--border-subtle)',
    position: 'relative',
    flexShrink: 0,
  },
  inputWrapper: {
    maxWidth: 900,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  composerCard: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 6,
    background: '#fff',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-xl)',
    padding: 6,
    boxShadow: 'var(--shadow-md)',
    fontFamily: 'var(--font-sans)',
    transition: 'border-color 200ms var(--ease-standard), box-shadow 200ms var(--ease-standard)',
  },
  input: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontFamily: 'var(--font-sans)',
    fontSize: 14.5,
    color: 'var(--fg-1)',
    background: 'transparent',
    resize: 'none',
    padding: '10px 6px',
    minHeight: 40,
    maxHeight: 200,
    lineHeight: 1.5,
  },
  sendButton: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'opacity 200ms, background 200ms',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: 'var(--shadow-sm)',
  },
  sendButtonDisabled: {
    background: 'var(--cg-neutral-100)',
    color: 'var(--fg-4)',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  stopButton: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'var(--danger)',
    color: '#fff',
    border: 'none',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 200ms',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: 'var(--shadow-sm)',
  },
  disclaimer: {
    textAlign: 'center',
    fontSize: 11,
    color: 'var(--fg-4)',
    marginTop: 2,
  },

  // ===== Drag overlay =====
  dragOverlay: {
    position: 'absolute',
    inset: 12,
    background: 'var(--accent-soft)',
    border: '2px dashed var(--accent)',
    borderRadius: 'var(--radius-xl)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    pointerEvents: 'none',
  },
  dragOverlayContent: {
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--accent-soft-fg)',
    background: '#fff',
    padding: '14px 24px',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-md)',
  },

  // ===== File chips =====
  fileChipsContainer: {
    maxWidth: 900,
    margin: '0 auto 10px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  fileChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 8px 5px 6px',
    background: '#fff',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-pill)',
    fontSize: 12,
    maxWidth: 280,
  },
  fileChipIcon: { fontSize: 14, flexShrink: 0 },
  fileChipName: {
    color: 'var(--fg-1)',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileChipSize: {
    color: 'var(--fg-4)',
    fontSize: 11,
    flexShrink: 0,
  },
  fileChipRemove: {
    background: 'transparent',
    border: 'none',
    color: 'var(--fg-3)',
    cursor: 'pointer',
    padding: 2,
    fontSize: 11,
    flexShrink: 0,
    lineHeight: 1,
  },

  // ===== Inline alerts =====
  fileError: {
    maxWidth: 900,
    margin: '0 auto 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    background: 'var(--danger-soft)',
    border: '1px solid var(--cg-terra-200)',
    borderRadius: 'var(--radius-md)',
    fontSize: 13,
    color: 'var(--danger)',
  },
  fileErrorClose: {
    background: 'transparent',
    border: 'none',
    color: 'var(--danger)',
    cursor: 'pointer',
    padding: 2,
    fontSize: 14,
    marginLeft: 'auto',
    lineHeight: 1,
  },
  fileSizeWarning: {
    maxWidth: 900,
    margin: '0 auto 10px',
    padding: '10px 14px',
    background: 'var(--warning-soft)',
    border: '1px solid var(--cg-clay-200)',
    borderRadius: 'var(--radius-md)',
    fontSize: 13,
    color: 'var(--warning)',
  },
  fileSizeInfo: {
    maxWidth: 900,
    margin: '0 auto 10px',
    padding: '10px 14px',
    background: 'var(--info-soft)',
    border: '1px solid var(--cg-teal-200)',
    borderRadius: 'var(--radius-md)',
    fontSize: 13,
    color: 'var(--info)',
  },

  // ===== Loading =====
  loadingContainer: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  loadingSpinner: {
    width: 36,
    height: 36,
    border: '3px solid var(--border-1)',
    borderTopColor: 'var(--accent)',
    borderRadius: '50%',
    animation: 'cgConicSpin 1s linear infinite',
  },
  loadingText: {
    fontSize: 14,
    color: 'var(--fg-3)',
    margin: 0,
  },

  // ===== Tool status =====
  toolStatusContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 14px',
    background: 'var(--bg-surface-1)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-xl)',
    borderTopLeftRadius: 'var(--radius-sm)',
    flex: 1,
    minWidth: 0,
  },
  toolStatusSpinner: {
    width: 16,
    height: 16,
    border: '2px solid var(--border-1)',
    borderTopColor: 'var(--accent)',
    borderRadius: '50%',
    animation: 'cgConicSpin 1s linear infinite',
    flexShrink: 0,
  },
  toolStatusContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  toolStatusText: {
    fontSize: 13,
    color: 'var(--fg-2)',
    fontWeight: 500,
  },
  toolStatusSubtext: {
    fontSize: 11.5,
    color: 'var(--fg-4)',
    fontWeight: 400,
  },

  // ===== Errors =====
  errorContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: 16,
    background: 'var(--danger-soft)',
    border: '1px solid var(--cg-terra-200)',
    borderRadius: 'var(--radius-lg)',
    maxWidth: 900,
    width: '100%',
    margin: '12px auto',
    boxShadow: 'var(--shadow-sm)',
  },
  errorContainerWarning: {
    background: 'var(--info-soft)',
    border: '1px solid var(--cg-teal-200)',
  },
  errorIcon: {
    fontSize: 20,
    flexShrink: 0,
    lineHeight: 1,
    marginTop: 1,
  },
  errorContent: { flex: 1, minWidth: 0 },
  errorHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
    flexWrap: 'wrap',
  },
  errorTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--danger)',
  },
  errorCode: {
    fontSize: 11,
    fontWeight: 500,
    color: 'var(--cg-terra-600)',
    background: 'rgba(138,46,24,0.10)',
    padding: '2px 6px',
    borderRadius: 'var(--radius-xs)',
    fontFamily: 'var(--font-mono)',
    letterSpacing: 0,
  },
  errorText: {
    fontSize: 13,
    color: 'var(--cg-terra-600)',
    lineHeight: 1.55,
    marginBottom: 8,
  },
  errorAction: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: 10,
    background: 'var(--info-soft)',
    border: '1px solid var(--cg-teal-200)',
    borderRadius: 'var(--radius-md)',
    marginTop: 8,
  },
  errorActionIcon: { fontSize: 14, flexShrink: 0 },
  errorActionText: {
    fontSize: 12.5,
    color: 'var(--info)',
    lineHeight: 1.5,
    fontWeight: 500,
  },
  errorCloseButton: {
    padding: 4,
    background: 'transparent',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontSize: 14,
    color: 'var(--danger)',
    transition: 'background 200ms',
    flexShrink: 0,
    fontFamily: 'var(--font-sans)',
    lineHeight: 1,
  },

  // ===== Generated images =====
  generatedImagesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 12,
  },
  generatedImageCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: 12,
    background: '#fff',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-lg)',
    maxWidth: 450,
    boxShadow: 'var(--shadow-xs)',
  },
  generatedImage: {
    maxWidth: '100%',
    maxHeight: 400,
    width: 'auto',
    height: 'auto',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-sm)',
    cursor: 'pointer',
    transition: 'transform 200ms, box-shadow 200ms',
  },
  imagePrompt: {
    fontSize: 12.5,
    color: 'var(--fg-3)',
    fontStyle: 'italic',
    lineHeight: 1.45,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2 as any,
    WebkitBoxOrient: 'vertical' as any,
  },
  imageActions: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  imageActionButton: {
    padding: '6px 12px',
    background: 'var(--cg-neutral-50)',
    color: 'var(--fg-1)',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 200ms',
    fontFamily: 'var(--font-sans)',
  },
};

export default ChatWindow;
